import { useSnackbar } from 'notistack';
import { ReactNode, useEffect } from 'react';
import {
  Link as RouterLink,
  useLocation,
  matchPath,
  useHistory
} from 'react-router-dom';
// material
import { experimentalStyled as styled } from '@material-ui/core/styles';
import {
  Box,
  Divider,
  Link,
  List,
  Button,
  Drawer,
  Hidden,
  Typography
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import baselineLogout from '@iconify/icons-ic/baseline-logout';
// hooks
import firebase from '../../firebase';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import useAuth from '../../hooks/useAuth';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// components
import Logo from '../../components/Logo';
import MyAvatar from '../../components/MyAvatar';
import Scrollbar from '../../components/Scrollbar';
//
import MenuLinks from './SidebarConfig';
import SidebarItem from './SidebarItem';
import useLocales from '../../hooks/useLocales';
import { Roles } from '../../constants/roles.enum';
import { DisableRoles } from '../../constants/disable-roles.enum';
import { setAdminVirtualCompanyId } from '../../redux/slices/authJwt';
import CustomLeftBar from '../../components/leftBar/leftBar';
import { getFeatures } from '../../redux/slices/features';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  margin: theme.spacing(1, 2.5, 5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12]
}));

// const DocStyle = styled('div')(({ theme }) => ({
//   padding: theme.spacing(2.5),
//   borderRadius: theme.shape.borderRadiusMd,
//   backgroundColor:
//     theme.palette.mode === 'light'
//       ? alpha(theme.palette.primary.main, 0.08)
//       : theme.palette.primary.lighter
// }));
// ----------------------------------------------------------------------

type TNavItem = {
  icon?: ReactNode;
  info?: ReactNode;
  href: string;
  title: string;
  items?: TNavItem[];
  role?: string | string[];
};

type ReduceChildParams = {
  array: ReactNode[];
  item: TNavItem;
  pathname: string;
  level: number;
};

function reduceChild({ array, item, pathname, level }: ReduceChildParams) {
  const key = item.href + level;

  if (item.items) {
    const match = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    return [
      ...array,
      <SidebarItem
        key={key}
        level={level}
        icon={item.icon}
        info={item.info}
        href={item.href}
        title={item.title}
        open={Boolean(match)}
      >
        {renderSidebarItems({
          pathname,
          level: level + 1,
          items: item.items
        })}
      </SidebarItem>
    ];
  }
  return [
    ...array,
    <SidebarItem
      key={key}
      level={level}
      href={item.href}
      icon={item.icon}
      info={item.info}
      title={item.title}
    />
  ];
}

type RenderSidebarItemsParams = {
  items: TNavItem[];
  pathname: string;
  level?: number;
};

function renderSidebarItems({
                              items,
                              pathname,
                              level = 0
                            }: RenderSidebarItemsParams) {
  return (
    <List disablePadding>
      {items.reduce<ReactNode[]>(
        (array, item) => reduceChild({ array, item, pathname, level }),
        []
      )}
    </List>
  );
}

type NavBarProps = {
  isOpenSidebar?: boolean;
  onCloseSidebar?: VoidFunction;
};

export default function DashboardSidebar({
                                           isOpenSidebar,
                                           onCloseSidebar
                                         }: NavBarProps) {
  const history = useHistory();
  const { pathname } = useLocation();
  const { translate } = useLocales();
  const { user, logout } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isOpenSidebar && onCloseSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  function setUserRoleToShow() {
    if (user) {
      if (user.roles.includes(Roles.SUPERADMIN)) {
        return DisableRoles.SUPERADMIN;
      }
      if (user.roles.includes(Roles.ADMIN)) {
        return DisableRoles.ADMIN;
      }
      if (user.roles.includes(Roles.USER)) {
        return DisableRoles.USER;
      }
      if (user.roles.includes(Roles.CLIENT_ADMIN)) {
        return DisableRoles.CLIENT_ADMIN;
      }
      if (user.roles.includes(Roles.FIRE_FIGHTER)) {
        return DisableRoles.FIRE_FIGHTER;
      }
      if (user.roles.includes(Roles.SUPERVISOR)) {
        return DisableRoles.SUPERVISOR;
      }
    }
    return '';
  }

  const handleLogout = async () => {
    try {
      await logout();

      const msg = firebase.messaging();
      await msg.deleteToken();

      if (user.roles.includes(Roles.ADMIN)) {
        await setAdminVirtualCompanyId(null);
      }

      // localStorage.setItem('deviceToken', null);
      history.push('/');
      if (isMountedRef.current) {
        history.push('/');
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  useEffect(() => {
    getFeatures();
  }, []);

  let linkTo = PATH_DASHBOARD.general.profile;

  if (user) {
    if (user.roles.includes(Roles.ADMIN)) {
      linkTo = PATH_DASHBOARD.general.companies;
    } else if (user.roles.includes(Roles.FIRE_FIGHTER)) {
      linkTo = PATH_DASHBOARD.general.fireFighter;
    }
  }

  const renderContent = (
    <Scrollbar>
      <Box sx={{ px: 0.5, py: 0.5 }}>
        {/* sx={{ px: 2.5, py: 3 }}> */}
        <RouterLink to={linkTo}>
          <Logo />
        </RouterLink>
      </Box>

      <Link
        underline="none"
        component={RouterLink}
        to={linkTo}
        // to={PATH_DASHBOARD.user.account}
      >
        <AccountStyle>
          <MyAvatar />
          <Box sx={{ ml: 2, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
              {user.email}
              {/* user.userName */}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {setUserRoleToShow()}
              {/* user.roles && user.roles.length >= 0 && user.roles[0] */}
            </Typography>
          </Box>
        </AccountStyle>
      </Link>

      {MenuLinks.map((list, key) => (
        <CustomLeftBar header={list} key={list.subheader} />
      ))}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ flexGrow: 1 }} />

      <Box
        sx={{
          px: 0,
          pt: 1.5,
          bottom: 0
        }}
      >
        <Button
          fullWidth
          sx={{ pl: 6, justifyContent: 'initial' }}
          color="inherit"
          variant="text"
          onClick={handleLogout}
          startIcon={<Icon icon={baselineLogout} />}
        >
          {translate('sidemenu.subheader.log.out')}
        </Button>
      </Box>
      {/*
      <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        <DocStyle>
          <Box
            component="img"
            alt="doc"
            src="/static/icons/ic_doc.svg"
            sx={{ width: 36, height: 36, mb: 2 }}
          />
          <Typography
            gutterBottom
            variant="subtitle1"
            sx={{ color: 'grey.800' }}
          >
            Hi, {user.displayName}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
            Need help?
            <br /> Please check our docs
          </Typography>
          <Button
            fullWidth
            to={PATH_DOCS.root}
            variant="contained"
            component={RouterLink}
          >
            Documentation
          </Button>
        </DocStyle>
      </Box> */}
    </Scrollbar>
  );

  return (
    <RootStyle>
      <Hidden lgUp>
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: { width: DRAWER_WIDTH, bgcolor: 'background.default' }
          }}
        >
          {renderContent}
        </Drawer>
      </Hidden>
    </RootStyle>
  );
}
